import { Code2, Copy } from 'lucide-react';
import { useMemo } from 'react';
import { toast } from 'sonner';

import {
  useGeneratePublicScheduleToken,
  usePublicScheduleToken,
} from '~entities/studios/api/public-schedule-token';

import { Button } from '~shared/shadcn/ui/button';
import { Input } from '~shared/shadcn/ui/input';
import { copyToClipboard } from '~shared/utils/clipboard';

function buildIframeEmbedHtml(embedEventsUrl: string) {
  return `<iframe
  title="Events"
  src="${embedEventsUrl}"
  width="100%"
  height="720"
  style="width:100%;min-height:600px;border:0"
  loading="lazy"
  referrerpolicy="no-referrer-when-downgrade"
></iframe>`;
}

export function PublicEventsLinkField() {
  const { data: tokenPayload, isLoading } = usePublicScheduleToken();
  const generateMutation = useGeneratePublicScheduleToken();
  const opaqueToken = tokenPayload?.token ?? null;

  const { eventsUrl, iframeHtml } = useMemo(() => {
    if (typeof opaqueToken !== 'string' || opaqueToken.trim().length === 0) {
      return {
        eventsUrl: '',
        iframeHtml: '',
      };
    }
    const rawBase = import.meta.env.VITE_PARENT_PORTAL_URL;
    if (typeof rawBase !== 'string' || rawBase.trim().length === 0) {
      return {
        eventsUrl: '',
        iframeHtml: '',
      };
    }
    const base = rawBase.replace(/\/$/, '');
    const url = `${base}/public-events?token=${encodeURIComponent(opaqueToken)}`;
    const embedUrl = `${url}&embedded=true`;
    return {
      eventsUrl: url,
      iframeHtml: buildIframeEmbedHtml(embedUrl),
    };
  }, [opaqueToken]);

  const handleCopyLink = async () => {
    if (!eventsUrl) {
      toast.error('Public events link not ready yet');
      return;
    }
    const success = await copyToClipboard(eventsUrl);
    if (success) {
      toast.success('Link copied!', {
        description:
          'The public events link has been copied to your clipboard.',
      });
    } else {
      toast.error('Failed to copy', {
        description: 'Please try copying the link manually.',
      });
    }
  };

  const handleCopyEmbed = async () => {
    if (!iframeHtml) {
      toast.error('Embed code not ready yet');
      return;
    }
    const success = await copyToClipboard(iframeHtml);
    if (success) {
      toast.success('Embed code copied!', {
        description: 'Paste it into an HTML or embed block on your website.',
      });
    } else {
      toast.error('Failed to copy', {
        description: 'Please try copying the embed code manually.',
      });
    }
  };

  const handleCreateToken = () => {
    generateMutation.mutate(undefined, {
      onSuccess: () => {
        toast.success('Public events link is ready', {
          description: 'Copy the link or embed code below.',
        });
      },
      onError: () => {
        toast.error('Could not create link', {
          description: 'Please try again or contact support.',
        });
      },
    });
  };

  const ready = Boolean(eventsUrl && iframeHtml);
  const needsToken =
    !isLoading && (opaqueToken == null || opaqueToken.trim().length === 0);

  return (
    <div className='space-y-2'>
      {needsToken ? (
        <div className='flex flex-wrap items-center gap-2'>
          <p className='text-sm text-muted-foreground'>
            Create a shareable link so families can browse your public events
            list without signing in.
          </p>
          <Button
            type='button'
            size='sm'
            onClick={handleCreateToken}
            disabled={generateMutation.isPending}
          >
            {generateMutation.isPending ? 'Creating…' : 'Create public link'}
          </Button>
        </div>
      ) : null}

      <div className='flex flex-wrap items-stretch gap-2'>
        <Input
          readOnly
          value={
            isLoading || generateMutation.isPending
              ? 'Loading…'
              : eventsUrl || '—'
          }
          className='min-w-0 flex-1 font-mono text-sm'
          disabled={!eventsUrl}
          aria-label='Public events page URL'
        />
        <div className='flex shrink-0 gap-2'>
          <Button
            type='button'
            variant='secondary'
            size='icon'
            onClick={handleCopyLink}
            aria-label='Copy public events link'
            title='Copy public events link'
            disabled={!eventsUrl}
          >
            <Copy className='size-4' />
          </Button>
          <Button
            type='button'
            variant='secondary'
            size='icon'
            onClick={handleCopyEmbed}
            aria-label='Copy embed code'
            title='Copy embed code (HTML iframe)'
            disabled={!ready}
          >
            <Code2 className='size-4' />
          </Button>
        </div>
      </div>
    </div>
  );
}
